import { DateTime } from "luxon";
import pLimit from "p-limit";

import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import {
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  StandardEventAllowedKeyMetrics,
  StandardEventAllowedKeyMetricsList,
} from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import {
  getEndedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-analytics/experience-status";

export const getInitialControl = (variations: VariationFe[]) => {
  const controlGroup = variations.find((group) => group.isControl);
  return controlGroup!.id;
};

export const getDefaultCompareGroupTb = (
  variations: VariationFe[],
  controlVariation: VariationFe,
  dashboardData: any[], // Tinybird data
  primaryMetricId: string
): string | undefined => {
  if (
    !dashboardData ||
    !variations?.length ||
    !controlVariation ||
    !primaryMetricId
  ) {
    return undefined;
  }

  const primaryMetricAnalyticsMetric =
    StandardEventAllowedKeyMetricsList.includes(
      primaryMetricId as StandardEventAllowedKeyMetrics
    )
      ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryMetricId].analyticsMetricId
      : primaryMetricId;
  let bestVariationId: string | undefined;
  let bestValue = -1;

  for (const group of dashboardData) {
    const variationId = group["variation_id"];
    if (variationId === controlVariation.id) {
      continue;
    }

    const rawValue = group[primaryMetricAnalyticsMetric];
    const value = Number(rawValue?.value ?? rawValue ?? 0);

    if (value > bestValue) {
      bestValue = value;
      bestVariationId = variationId;
    }
  }
  // Fallback if no variation had a value > -1
  if (!bestVariationId) {
    const defaultGroup = variations.find((v) => !v.isControl);
    return defaultGroup?.id;
  }

  return bestVariationId;
};

interface ExecuteChunkedOptions<S, T> {
  data: S[];
  chunkSize?: number;
  concurrency?: number;
  execute: (items: S[]) => Promise<T[]>;
}

export async function executeChunked<S, T>({
  data,
  chunkSize = 250,
  concurrency = 10,
  execute,
}: ExecuteChunkedOptions<S, T>) {
  if (!data || data.length === 0) {
    return [];
  }

  const chunks: S[][] = [];
  let chunk: S[] = [];
  for (const item of data) {
    if (chunk.length >= chunkSize) {
      chunks.push(chunk);
      chunk = [];
    }
    chunk.push(item);
  }
  if (chunk.length > 0) {
    chunks.push(chunk);
  }

  const limit = pLimit(concurrency);
  const queue = chunks.map(async (chunk) => limit(async () => execute(chunk)));
  const result = await Promise.all(queue);
  return result.flat();
}

export const getDefaultHiddenEndTime = (
  experience: Pick<ExperienceFe, "status" | "experienceActions">
) => {
  if (experience.status === "paused") {
    return getPausedAtTs(experience.experienceActions)!.actionTs;
  } else if (experience.status === "ended") {
    return getEndedAtTs(experience.experienceActions)!.actionTs;
  } else {
    return DateTime.now().toMillis();
  }
};

export const getDefaultPersonalizationStartTime = (
  experience: ExperienceFe,
  timezone?: string
) => {
  const endTime = getDefaultHiddenEndTime(experience);

  let startTime =
    getStartedAtTs(experience.experienceActions)?.actionTs || Date.now();

  const diffInDays = (endTime - startTime) / (1000 * 3600 * 24);

  if (diffInDays > 30) {
    startTime = DateTime.fromMillis(endTime)
      .setZone(timezone)
      .minus({ day: 30 })
      .startOf("day")
      .toMillis();
  }

  return startTime;
};
